import React, { useState, useEffect } from 'react';
import { Tab, Tabs, Card, Row, Col } from 'react-bootstrap';
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  ResponsiveContainer,
} from 'recharts';
import { getLanguageName } from '../utils/languageUtils';
import '../styles/AudienceSection.css';

const AudienceDemographics = ({ audienceLanguage, audienceEthnicity, audienceAge }) => {
  const [activeTab, setActiveTab] = useState('Age');
  const [data, setData] = useState({ Age: [], Language: [], Ethnicity: [] });

  useEffect(() => {
    const newData = {};

    if (audienceAge?.length) {
      const ageData = audienceAge.reduce((acc, item) => {
        const existing = acc.find(entry => entry.name === item.age_range);
        if (existing) {
          existing[item.gender.toLowerCase()] = item.value;
        } else {
          acc.push({
            name: item.age_range,
            [item.gender.toLowerCase()]: item.value,
          });
        }
        return acc;
      }, []);
      newData.Age = ageData;
    }

    if (audienceLanguage?.length) {
      newData.Language = audienceLanguage
        .filter(item => item.value >= 0.5)
        .map(item => ({
          name: getLanguageName(item.code),
          value: item.value,
        }));
    }

    if (audienceEthnicity?.length) {
      newData.Ethnicity = audienceEthnicity.map(item => ({
        name: item.name,
        value: item.value,
      }));
    }

    setData(newData);
    const firstAvailableTab = Object.keys(newData)[0];
    if (firstAvailableTab) setActiveTab(firstAvailableTab);
  }, [audienceAge, audienceLanguage, audienceEthnicity]);

  const visibleTabs = Object.keys(data);
  if (!visibleTabs.length) return null;

  const renderLegend = () => (
    <div className="d-flex align-items-center gap-3">
      <div className="d-flex align-items-center gap-2">
        <div style={{ width: 12, height: 12, backgroundColor: '#8984D8', borderRadius: '50%' }} />
        <small className="text-muted">Male</small>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div style={{ width: 12, height: 12, backgroundColor: '#83CA9D', borderRadius: '50%' }} />
        <small className="text-muted">Female</small>
      </div>
    </div>
  );

  const renderChart = () => {
    if (!data[activeTab]) return null;

    return (
      <div className='demographics-legend audience-border-radius p-3'>
        {activeTab === 'Age' ? (
          <Row className="align-items-center justify-content-between mb-3">
            <Col xs="auto">
              <div>
                <h6 className="sub-heading mb-0">Followers Growth</h6>
                <small className="sub-text text-muted">Trend of total followers</small>
              </div>
            </Col>
            <Col xs="auto">{renderLegend()}</Col>
          </Row>
        ) : (<Row className="align-items-center justify-content-center mb-3">
          <Col xs="auto">
          </Col>
          <Col xs="auto"><div className="d-flex align-items-center gap-2">
            <div style={{ width: 12, height: 12, backgroundColor: '#8984D8', borderRadius: '50%' }} />
            <small className="text-muted">Value</small>
          </div></Col>
        </Row>)}

        <ResponsiveContainer width="104%" height={450}>
          <BarChart
            data={data[activeTab]}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12 }}
              angle={activeTab === 'Language' ? -15 : 0}
              textAnchor="end"
              interval={0}
            />
            <YAxis tickCount={10} />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                boxShadow: '0px 0px 8px rgba(0,0,0,0.1)',
              }}
              itemStyle={{ padding: '5px 8px', borderRadius: 4 }}
            />
            {activeTab === 'Age' ? (
              <>
                <Bar dataKey="male" stackId="gender" fill="#8984D8" name="Male" radius={[0, 0, 0, 0]} />
                <Bar dataKey="female" stackId="gender" fill="#83CA9D" name="Female" radius={[10, 10, 0, 0]} />
              </>
            ) : (
              <Bar dataKey="value" fill="#8984D8" radius={[10, 10, 0, 0]} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <Card className="audience-demographics-container border rounded-0 active">
      <Card.Body>
        <div className="info-header-title">Audience Demographics</div>
        <Tabs
          id="audience-demographics-tabs"
          className="custom-tab-container my-3"
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
        >
          {visibleTabs.map(tab => (
            <Tab eventKey={tab} title={tab} key={tab} />
          ))}
        </Tabs>
        {renderChart()}
      </Card.Body>
    </Card>
  );
};

export default AudienceDemographics;
